import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { Play, Check } from "lucide-react";
import { useStore } from "../../context/StoreContext";

const MaskedLine = ({ text, delay }) => (
  <span className="block overflow-hidden">
    <motion.span
      className="block"
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.9, delay, ease: [0.16, 1, 0.3, 1] }}
    >
      {text}
    </motion.span>
  </span>
);

export const Hero = () => {
  const { config, openCart } = useStore();
  const mediaRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: mediaRef, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], [30, -30]);

  const headline = config?.hero_headline || "Your pet stays. Their hair doesn't.";
  const lines = headline.split(/(?<=\.)\s+/);
  const media = config?.media || {};

  const scrollToPacks = () => {
    document.getElementById("packs")?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="top" data-testid="hero-section" className="relative overflow-hidden">
      <div className="absolute -top-32 -right-32 w-[480px] h-[480px] rounded-full bg-brand-soft blur-3xl -z-10" />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-10 pb-16 sm:pt-16 sm:pb-24">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-16 items-center">
          <div className="lg:col-span-7">
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="font-mono-brand text-xs text-brand mb-6 tracking-wide"
              data-testid="hero-chapter"
            >
              01 — REUSABLE SELF-CLEANING ROLLER
            </motion.p>
            <h1
              data-testid="hero-headline"
              className="font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.08] text-balance"
            >
              {lines.map((line, i) => (
                <MaskedLine key={i} text={line} delay={0.25 + i * 0.15} />
              ))}
            </h1>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.6 }}
              className="mt-6 text-base sm:text-lg text-zinc-600 leading-relaxed max-w-xl"
              data-testid="hero-subheadline"
            >
              {config?.hero_subheadline}
            </motion.p>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.85, duration: 0.6 }}
              className="mt-8"
            >
              <motion.button
                data-testid="hero-cta-button"
                onClick={scrollToPacks}
                whileHover={{ y: -2 }}
                whileTap={{ scale: 0.98 }}
                className="w-full sm:w-auto bg-brand hover:bg-brand-dark text-white font-display font-bold text-base sm:text-lg rounded-full px-10 py-4 sm:py-5 shadow-lg shadow-brand/30 transition-colors"
              >
                {config?.hero_cta || "QUIERO MI PELOCERO"}
              </motion.button>
              <div className="mt-5 flex flex-wrap gap-x-5 gap-y-2 text-sm text-zinc-600" data-testid="hero-trust-ticks">
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-green-600" /> Reusable</span>
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-green-600" /> No refills</span>
                <span className="flex items-center gap-1.5"><Check className="w-4 h-4 text-green-600" /> Easy to empty</span>
              </div>
            </motion.div>
          </div>
          <div className="lg:col-span-5" ref={mediaRef}>
            <motion.div
              initial={{ opacity: 0, scale: 0.96 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.4, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="relative"
            >
              <div className="absolute inset-0 translate-x-4 translate-y-4 rounded-[2.5rem] bg-brand-soft -z-10" />
              <div className="rounded-[2.5rem] overflow-hidden aspect-[4/5] bg-zinc-100 shadow-2xl shadow-zinc-900/10 ring-1 ring-zinc-900/5">
                {media.hero_video ? (
                  <video
                    data-testid="hero-video"
                    src={media.hero_video}
                    autoPlay
                    muted
                    loop
                    playsInline
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <motion.img
                    data-testid="hero-image"
                    src={media.hero_image}
                    alt="PeloCero removing pet hair from a sofa"
                    style={{ y }}
                    loading="eager"
                    className="w-full h-[112%] object-cover"
                  />
                )}
              </div>
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 1.1 }}
                className="absolute -bottom-5 -left-3 sm:-left-6 bg-white rounded-2xl shadow-xl shadow-zinc-900/10 px-5 py-4 flex items-center gap-3 ring-1 ring-zinc-100"
              >
                <span className="w-9 h-9 rounded-full bg-brand-soft flex items-center justify-center">
                  <Play className="w-4 h-4 text-brand" />
                </span>
                <div>
                  <p className="text-sm font-semibold">One pass and done</p>
                  <p className="text-xs text-zinc-500">No refills. No adhesives.</p>
                </div>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
};
